import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ExternalLink } from 'lucide-react';
import api from '../../../api';
import './AccountSections.css';

const MyBrandsSection = ({ userId }) => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get stored userId and role for debugging
        const storedUserId = localStorage.getItem('userId');
        const storedRole = localStorage.getItem('role');
        const token = localStorage.getItem('token');

        // Validate authentication
        if (!token) {
          setError('No authentication token found. Please log in.');
          return;
        }

        if (!storedUserId) {
          setError('User ID not found. Please log in again.');
          return;
        }

        // CRITICAL: Always use the stored userId, not the route param
        const userIdToUse = storedUserId;

        const response = await api.get(`/brands/${userIdToUse}/brands`);
        setBrands(response.data || []);
      } catch (err) {

        let errorMessage = 'Failed to load brands data';

        if (err.response) {
          if (err.response.status === 403) {
            if (err.response.data && typeof err.response.data === 'object' && err.response.data.message) {
              errorMessage = err.response.data.message;
            } else {
              errorMessage = 'Access denied. Please verify you are logged in with a brand account.';
            }
          } else if (err.response.status === 404) {
            errorMessage = 'Brand not found. Please contact support.';
          } else if (err.response.data) {
            if (typeof err.response.data === 'object' && err.response.data.message) {
              errorMessage = err.response.data.message;
            } else if (typeof err.response.data === 'string') {
              errorMessage = err.response.data;
            }
          }
        } else if (err.request) {
          errorMessage = 'Cannot connect to server. Please check your connection.';
        } else if (err.message) {
          errorMessage = err.message;
        }

        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    if (userId || localStorage.getItem('userId')) {
      fetchBrands();
    }
  }, [userId]);

  const handleAddBrand = () => {
    // TODO: Implement add brand modal/form
    alert('Add brand functionality coming soon!');
  };

  const handleEditBrand = (brandId) => {
    // TODO: Implement edit brand functionality
  };

  const handleDeleteBrand = (brandId) => {
    // TODO: Implement delete brand functionality
    if (window.confirm('Are you sure you want to delete this brand?')) {
      setBrands(brands.filter(brand => brand.id !== brandId));
    }
  };

  return (
    <div className="section-container">
      <div className="section-header">
        <div>
          <h2 className="section-title">My Brands</h2>
          <p className="section-description">Manage your registered brands</p>
        </div>
        <button onClick={handleAddBrand} className="btn-primary">
          <Plus size={18} className="inline mr-2" />
          Add New Brand
        </button>
      </div>

      {loading && (
        <div className="loading-state">
          <p>Loading brands...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <p className="error-message">{error}</p>
        </div>
      )}

      {!loading && !error && brands.length > 0 && (
        <div className="brands-grid">
          {brands.map((brand) => (
            <div key={brand.id} className="brand-card">
              <div className="brand-card-header">
                <img
                  src={brand.logo || '/collaborb-logo.png'}
                  alt={brand.name}
                  className="brand-logo"
                />
                <span className={`status-badge ${brand.status.toLowerCase()}`}>
                  {brand.status}
                </span>
              </div>

              <div className="brand-card-body">
                <h3 className="brand-name">{brand.name}</h3>
                <p className="brand-category">{brand.category}</p>
                {brand.website && (
                  <a
                    href={brand.website.startsWith('http') ? brand.website : `https://${brand.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="brand-website"
                  >
                    <ExternalLink size={14} className="inline mr-1" />
                    {brand.website}
                  </a>
                )}
              </div>

              <div className="brand-card-actions">
                <button
                  onClick={() => handleEditBrand(brand.id)}
                  className="btn-icon"
                  title="Edit brand"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDeleteBrand(brand.id)}
                  className="btn-icon btn-danger"
                  title="Delete brand"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !error && brands.length === 0 && (
        <div className="empty-state">
          <p className="empty-state-text">No brands registered yet</p>
          <button onClick={handleAddBrand} className="btn-primary">
            <Plus size={18} className="inline mr-2" />
            Add Your First Brand
          </button>
        </div>
      )}
    </div>
  );
};

export default MyBrandsSection;

